import { useEffect } from "react";


import {
  Eye,
  CalendarDays,
  Plus,
  FileText,
  Trash2,
} from "lucide-react";

import EyeTestFields from "./EyeTestFields";
import NewLensesFields from "./NewLensesFields";
import NewSpectaclesFields from "./NewSpectaclesFields";
import RepairFields from "./RepairFields";
import { OPTION_MAP, OpticalCombo } from "./opticalOptions";

const Prescription = ({
  title,
  data,
  index,
  setPrescriptions,
  canRemove,
  onRemove,
  visitFor,
}) => {
  const handleChange = (e) => {
    const { name, value } = e.target;

    setPrescriptions((prev) =>
      prev.map((item, i) =>
        i === index
          ? {
              ...item,
              [name]: value,
            }
          : item
      )
    );
  };

const handleImageChange = (e) => {
  const files = Array.from(e.target.files);

  const MAX_IMAGES = 10;
  const existingImages = data.images || [];

  if (existingImages.length + files.length > MAX_IMAGES) {
    alert(`Maximum ${MAX_IMAGES} images allowed.`);
    e.target.value = "";
    return;
  }

  const validFiles = files.filter((file) => {
    const validType = [
      "image/jpeg",
      "image/png",
      "image/webp",
    ].includes(file.type);

    const validSize = file.size <= 10 * 1024 * 1024;

    return validType && validSize;
  });

  if (validFiles.length === 0) {
    alert("Please select only JPG, PNG or WEBP images up to 10MB.");
    e.target.value = "";
    return;
  }

  const previews = validFiles.map((file) => ({
    id: crypto.randomUUID(),
    file,
    preview: URL.createObjectURL(file),
  }));

  setPrescriptions((prev) =>
    prev.map((item, i) =>
      i === index
        ? {
            ...item,
            images: [...(item.images || []), ...previews],
          }
        : item
    )
  );

  e.target.value = "";
};

  const removeImage = (id) => {
  setPrescriptions((prev) =>
    prev.map((item, i) => {
      if (i !== index) return item;

      const images = item.images || [];

      const imageToRemove = images.find((img) => img.id === id);

      if (imageToRemove?.preview) {
        URL.revokeObjectURL(imageToRemove.preview);
      }

      return {
        ...item,
        images: images.filter((img) => img.id !== id),
      };
    })
  );
};

useEffect(() => {
  return () => {
    (data.images || []).forEach((img) => {
      if (img.preview) {
        URL.revokeObjectURL(img.preview);
      }
    });
  };
}, [data.images]);

return (
  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">

    {/* Header */}
    <div className="bg-[#2B1B14] px-6 py-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-lg bg-[#1E120D] flex items-center justify-center">
          <Eye
  size={20}
  aria-hidden="true"
  className="text-white"
/>
        </div>

        <div>
          <h2 className="text-white text-xl font-semibold">
            {title}
          </h2>

          <p className="text-gray-300 text-sm">
            {visitFor === "Eye Test"
              ? "Clinical eye test chart & scans"
              : visitFor === "Lens Only"
              ? "Prescription + lens order details"
              : visitFor === "Complete Specs"
              ? "Prescription + full spectacles order"
              : visitFor === "Specs Repair"
              ? "Repair details & attachments"
              : "Eye prescription details & uploaded eyewear images"}
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-3 md:flex-row md:items-center">
  <div className="relative">
    <CalendarDays
  size={18}
  aria-hidden="true"
  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
/>

    <input
      type="date"
      name="prescription_date"
      value={data.prescription_date || ""}
      onChange={handleChange}
      max={new Date().toISOString().split("T")[0]}
      className="h-11 rounded-lg border border-[#4D3429] bg-[#3B241A] pl-10 pr-4 text-white outline-none focus:border-[#8B5E3C] focus:ring-2 focus:ring-[#8B5E3C]/20"
    />
  </div>

  {canRemove && (
    <button
      type="button"
      onClick={onRemove}
      className="h-11 px-4 rounded-lg bg-red-500 hover:bg-red-600 text-white flex items-center gap-2"
    >
     <Trash2
  size={18}
  aria-hidden="true"
/>
      Remove
    </button>
  )}
</div>
    </div>

    <div className="p-6 space-y-5">

      {/* Basic prescription grid — hidden for Eye Testing & Repair */}
      {visitFor !== "Eye Test" && visitFor !== "Specs Repair" && (
      <>
      {/* Eye Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

        {/* Right Eye */}
        <div className="border rounded-2xl p-5">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-lg bg-[#2B1B14] flex items-center justify-center">
              <Eye
  size={18}
  aria-hidden="true"
  className="text-white"
/>
            </div>

            <div>
              <h3 className="font-semibold text-gray-800">
                Right Eye
              </h3>

              <p className="text-xs text-gray-500">
                Right eye prescription details
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">

            <div>
              <label className="text-xs font-medium text-gray-500">
                SPH
              </label>

              <OpticalCombo
                name="right_sph"
                value={data.right_sph}
                onChange={handleChange}
                options={OPTION_MAP.sph}
                placeholder="SPH"
                className="w-full h-11 mt-1 border rounded-lg px-3 bg-white focus:ring-2 focus:ring-blue-100 outline-none"
                testId="rx-right_sph"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-gray-500">
                CYL
              </label>

              <OpticalCombo
                name="right_cyl"
                value={data.right_cyl}
                onChange={handleChange}
                options={OPTION_MAP.cyl}
                placeholder="CYL"
                className="w-full h-11 mt-1 border rounded-lg px-3 bg-white focus:ring-2 focus:ring-blue-100 outline-none"
                testId="rx-right_cyl"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-gray-500">
                AX
              </label>

              <OpticalCombo
  name="right_axis"
                value={data.right_axis}
                onChange={handleChange}
                options={OPTION_MAP.axis}
                placeholder="AX"
                className="w-full h-11 mt-1 border rounded-lg px-3 bg-white focus:ring-2 focus:ring-blue-100 outline-none"
                testId="rx-right_axis"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-gray-500">
                VN
              </label>

              <OpticalCombo
                name="right_vn"
                value={data.right_vn}
                onChange={handleChange}
                options={OPTION_MAP.vn}
                placeholder="VN"
                className="w-full h-11 mt-1 border rounded-lg px-3 bg-white focus:ring-2 focus:ring-blue-100 outline-none"
                testId="rx-right_vn"
              />
            </div>

          </div>
        </div>

        {/* Left Eye */}
        <div className="border rounded-2xl p-5">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-lg bg-[#2B1B14] flex items-center justify-center">
             <Eye
  size={18}
  aria-hidden="true"
  className="text-white"
/>
            </div>

            <div>
              <h3 className="font-semibold text-gray-800">
                Left Eye
              </h3>

              <p className="text-xs text-gray-500">
                Left eye prescription details
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">

            <div>
              <label className="text-xs font-medium text-gray-500">
                SPH
              </label>

              <OpticalCombo
                name="left_sph"
                value={data.left_sph}
                onChange={handleChange}
                options={OPTION_MAP.sph}
                placeholder="SPH"
                className="w-full h-11 mt-1 border rounded-lg px-3 bg-white focus:ring-2 focus:ring-blue-100 outline-none"
                testId="rx-left_sph"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-gray-500">
                CYL
              </label>

              <OpticalCombo
                name="left_cyl"
                value={data.left_cyl}
                onChange={handleChange}
                options={OPTION_MAP.cyl}
                placeholder="CYL"
                className="w-full h-11 mt-1 border rounded-lg px-3 bg-white focus:ring-2 focus:ring-blue-100 outline-none"
                testId="rx-left_cyl"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-gray-500">
                AX
              </label>

              <OpticalCombo
  name="left_axis"
                value={data.left_axis}
                onChange={handleChange}
                options={OPTION_MAP.axis}
                placeholder="AX"
                className="w-full h-11 mt-1 border rounded-lg px-3 bg-white focus:ring-2 focus:ring-blue-100 outline-none"
                testId="rx-left_axis"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-gray-500">
                VN
              </label>

              <OpticalCombo
                name="left_vn"
                value={data.left_vn}
                onChange={handleChange}
                options={OPTION_MAP.vn}
                placeholder="VN"
                className="w-full h-11 mt-1 border rounded-lg px-3 bg-white focus:ring-2 focus:ring-blue-100 outline-none"
                testId="rx-left_vn"
              />
            </div>

          </div>
        </div>

      </div>

      {/* Bottom Inputs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

        <div className="border rounded-2xl p-5">
          <label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-3">
            <Plus
  size={16}
  aria-hidden="true"
/>
            Add Power
          </label>

          <OpticalCombo
  name="add_power"
  value={data.add_power}
  onChange={handleChange}
  options={OPTION_MAP.add}
  placeholder="Select add power"
  className="w-full h-11 border rounded-lg px-3 bg-white outline-none focus:ring-2 focus:ring-blue-100"
  testId="rx-add_power"
/>
        </div>

        <div className="border rounded-2xl p-5">
          <label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-3">
            <FileText
  size={16}
  aria-hidden="true"
/>
            Remarks
          </label>

          <textarea
  rows={3}
  name="remarks"
  value={data.remarks || ""}
  onChange={handleChange}
  maxLength={500}
  placeholder="Enter remarks"
  className="w-full resize-none rounded-lg border px-3 py-3 outline-none focus:ring-2 focus:ring-blue-100"
/>
        </div>

      </div>
      </>
      )}

{/* Eye Testing extra clinical chart */}
{visitFor === "Eye Test" && (
  <EyeTestFields data={data} onChange={handleChange} />
)}

{/* New Lenses order fields */}
{visitFor === "Lens Only" && (
  <NewLensesFields data={data} onChange={handleChange} />
)}

{/* New Spectacles order fields */}
{visitFor === "Complete Specs" && (
  <NewSpectaclesFields data={data} onChange={handleChange} />
)}

{/* Spectacles Repair fields */}
{visitFor === "Specs Repair" && (
  <RepairFields data={data} onChange={handleChange} />
)}

{/* Upload Eye Glass Images */}
<div className="border rounded-2xl p-5">
  <div className="flex items-center justify-between mb-5">
    <div>
      <h3 className="text-lg font-semibold text-gray-800">
        Upload Eye Glass Images
      </h3>

      <p className="text-sm text-gray-500">
        Upload multiple customer eyewear or frame images
      </p>
    </div>

    <span className="px-3 py-1 rounded-full bg-gray-100 text-xs font-medium text-gray-600">
      Multiple Upload Supported
    </span>
  </div>

  <label className="border-2 border-dashed border-gray-300 rounded-2xl h-72 flex flex-col items-center justify-center cursor-pointer hover:border-[#2B1B14] transition">
    <input
  type="file"
  multiple
  accept=".jpg,.jpeg,.png,.webp"
  className="hidden"
  onChange={handleImageChange}
/>

    <div className="w-16 h-16 rounded-2xl bg-[#2B1B14] flex items-center justify-center mb-5">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="w-8 h-8 text-white"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1M16 8l-4-4m0 0L8 8m4-4v12"
        />
      </svg>
    </div>

    <h4 className="text-lg font-semibold text-gray-800">
      Upload Images
    </h4>

    {(data.images || []).length > 0 && (
      <p className="mt-2 text-sm font-medium text-green-600">
        {(data.images || []).length} Image
        {(data.images || []).length > 1 ? "s" : ""} Selected
      </p>
    )}

    <p className="mt-2 px-5 text-center text-sm text-gray-500">
      Upload multiple JPG, PNG or WEBP customer eyewear images
    </p>
  </label>

  {(data.images || []).length > 0 && (
    <div className="mt-6">
      <h4 className="mb-4 text-lg font-semibold">
        Uploaded Images ({(data.images || []).length})
      </h4>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-5">
        {(data.images || []).map((image) => (
          <div
            key={image.id}
            className="relative overflow-hidden rounded-xl border bg-white"
          >
            <img
              src={image.preview}
              alt="Customer"
              className="h-40 w-full object-cover"
            />

            <button
              type="button"
              onClick={() => removeImage(image.id)}
              className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-red-500 text-white shadow transition hover:bg-red-600"
            >
              ✕
            </button>
          </div>
        ))}
      </div>
    </div>
  )}
</div>

      </div>
      
    </div>
  );
};

export default Prescription;